import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { prop } from 'styled-tools'
import tracker from '../../../utils/tracker'
import { Headline, Paragraph } from '../text'
import { useVerification } from './useVerification'
import { CheckCircle } from '../icons/checkCircle'
import { EditIcon } from '../icons/edit'
import Loader from '../loader/Loader'

const Wrapper = styled.div`
  max-width: 600px;
  min-height: 400px;
  margin: 16px;
  background-color: #fff;
  border-radius: 15px;
  padding: 32px;
  gap: 32px;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-grow: 1;
  text-align: center;
  justify-content: center;

  @media (max-width: 600px) {
    gap: 24px;
  }
`

const Background = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #000000d8;
  z-index: 1001;

  display: flex;
  align-items: center;
  justify-content: center;
`

const Loading = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const CodeItems = styled.div`
  position: relative;
  display: flex;
  gap: 16px;
  font-size: 32px;
  line-height: 1;
`

const CodeItem = styled.input`
  border-radius: 8px;
  border: 1px solid #19222833;
  width: 74px;
  height: 74px;
  text-align: center;

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  -moz-appearance: textfield;

  animation: ${(p) => p.$shake && 'shaking 0.5s linear 0s 1'};

  @media (max-width: 600px) {
    width: 64px;
    height: 64px;
  }

  &:focus-visible {
    outline: none;
  }

  &:valid {
    border-color: #000;
  }

  @keyframes shaking {
    0% {
      padding-left: 0rem;
    }
    12.5% {
      padding-left: 1rem;
    }
    37.5% {
      padding-left: -1rem;
    }
    50% {
      padding-left: 0rem;
      font-size: 32px;
    }
    100% {
      font-size: 0px;
    }
  }
`

const Heading = styled(Headline)`
  margin: 0;
`
const Text = styled(Paragraph)`
  margin: 0;
`

const Link = styled.a`
  color: ${prop('color', prop('theme.typography.link.color'))};
  text-decoration: ${prop('theme.typography.link.textDecoration')};
`

const Error = styled.div`
  width: 344px;
  border: 1px solid #a04649;
  background-color: #a0464926;
  padding: 8px 16px;
  text-align: left;
  @media (max-width: 600px) {
    width: 304px;
  }
`

const ErrorText = styled(Text)`
  color: #a04649;
`

const Email = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  text-decoration: none;
  color: inherit;
`

const VERIFICATION_REQUIRED = 'verification-required'
const CODE_MISMATCH = 'code-mismatch'

export const Verification = ({
  show: enabled,
  email,
  phoneNumber,
  storeId,
  storeName,
  meetingType,
  onConfirmed,
  onClose: close,
}) => {
  const verificationRequiredSent = useRef()
  const {
    message,
    success,
    token,
    loading,
    type: serviceType,
    id: verificationId,
    storeVerification,
    meetingTypeVerification,
    verify,
    resendCode,
  } = useVerification({
    enabled,
    email,
    phoneNumber,
    storeId,
    meetingType,
  })
  const [code, setCode] = useState(['', '', '', ''])
  const valid = useMemo(() => code.join('').length === 4, [code])
  const [sent, setSent] = useState(false)
  const [clearing, setClearing] = useState(false)
  const refs = useRef([])
  const [show, setShow] = useState(false)
  const verificationRequired = useMemo(
    () => message === VERIFICATION_REQUIRED || message === CODE_MISMATCH,
    [message]
  )
  const [manual, setManual] = useState(false)

  useEffect(() => {
    if (show) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
    }
  }, [show])

  // Cleanup overflow to make sure we don't have it lingering after close
  useEffect(
    () => () => {
      document.documentElement.style.overflow = ''
    },
    []
  )

  useEffect(() => {
    if (show && !enabled && !success) {
      setShow(false)
    } else if (!show && enabled) {
      setShow(verificationRequired)
    }
  }, [verificationRequired, show, enabled, success])

  useEffect(() => {
    if (verificationRequiredSent.current) {
      return
    }
    if (verificationRequired) {
      verificationRequiredSent.current = true
      tracker.trackVerificationRequired(storeName, verificationId, serviceType)
    }
  }, [
    verificationRequired,
    verificationId,
    verificationRequiredSent,
    serviceType,
    storeName,
  ])

  useEffect(() => {
    if (token && success) {
      onConfirmed(
        token,
        verificationId,
        verificationRequiredSent.current,
        storeVerification || meetingTypeVerification
      )
    }
  }, [
    token,
    success,
    onConfirmed,
    verificationRequired,
    storeVerification,
    meetingTypeVerification,
  ])

  const focus = useCallback(
    (idx) => {
      const ref = refs.current[idx]
      if (ref) {
        ref.focus()
      }
    },
    [refs]
  )

  useEffect(() => {
    const empty = !code.join('').length
    if (empty) {
      focus(0)
    }
  }, [code])

  const onClose = useCallback(
    (e) => {
      e.preventDefault()
      close()
    },
    [close]
  )

  const onChange = useCallback((e, idx) => {
    const value = parseInt(e.target.value, 10)
    setManual(false)
    if (isNaN(value)) {
      setCode((c) => [...c.slice(0, idx), '', ...c.slice(idx + 1)])
    } else {
      setCode((c) => [...c.slice(0, idx), `${value % 10}`, ...c.slice(idx + 1)])
      focus(idx + 1)
    }
  }, [])

  const onVerify = useCallback(() => {
    setSent(false)
    setManual(false)
    verify({ code: code.join('') }).then((result) => {
      if (result.success) {
        tracker.trackVerificationSuccess(storeName, verificationId, serviceType)
      } else {
        tracker.trackVerificationCodeFailed(
          storeName,
          verificationId,
          serviceType
        )
        setClearing(true)
        setTimeout(() => {
          setCode(['', '', '', ''])
          setClearing(false)
        }, 480)
      }
    })
  }, [
    verify,
    code,
    serviceType,
    storeName,
    verificationId,
    storeVerification,
    meetingTypeVerification,
  ])

  const onResend = useCallback(
    (e) => {
      e.preventDefault()
      resendCode()
      setSent(true)
      tracker.trackVerificationResendCode(
        storeName,
        verificationId,
        serviceType
      )
    },
    [resendCode, storeName, verificationId, serviceType]
  )

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && valid) {
        onVerify()
        return
      }

      const ignoreKeys = ['Tab']
      if (ignoreKeys.includes(e.key)) {
        return
      }

      if (e.key === 'v' && e.ctrlKey) {
        return
      }

      const focused = refs.current.findIndex(
        (c) => c === document.activeElement
      )
      const setValue = (idx, diff) => {
        if (typeof code[idx] === 'undefined') {
          return
        }
        const copy = [...code]
        if (typeof diff === 'undefined') {
          copy[focused] = ''
        } else {
          const value = copy[idx] === '' ? -diff : parseInt(copy[idx], 10) || 0
          const newValue = (value + 10 + diff) % 10
          copy[focused] = `${newValue}`
        }
        setCode(copy)
      }

      const navigationKeys = [
        'Backspace',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
      ]
      if (focused >= 0 && focused < 4 && navigationKeys.includes(e.key)) {
        if (e.key === 'ArrowLeft') {
          focus(focused - 1)
        } else if (e.key === 'ArrowRight') {
          focus(focused + 1)
        } else if (e.key === 'ArrowUp') {
          setManual(true)
          setValue(focused, 1)
        } else if (e.key === 'ArrowDown') {
          setManual(true)
          setValue(focused, -1)
        } else if (e.key === 'Backspace') {
          const value = code[focused]
          if (value) {
            setValue(focused)
          } else {
            setValue(focused - 1)
            focus(focused - 1)
          }
        }
        e.preventDefault()
        return
      }

      const num = parseInt(e.key, 10)
      if (isNaN(num)) {
        e.preventDefault()
      }
    },
    [valid, onVerify, code, refs, focus]
  )

  const onPaste = useCallback((e) => {
    const chars = e.clipboardData.getData('text').trim().split('')
    const isNumber = chars.slice(0, 4).every((c) => !isNaN(parseInt(c, 10)))
    if (chars.length === 4 && isNumber) {
      setCode(chars)
    } else {
      e.preventDefault()
    }
  }, [])

  useEffect(() => {
    if (valid && !manual) {
      onVerify()
    }
  }, [valid, onVerify, manual])

  if (!show) {
    return <></>
  }

  return (
    <Background>
      <Wrapper>
        {success && (
          <>
            <CheckCircle />
            <Heading>Booking confirmed</Heading>
            <Paragraph>Arranging your booking...</Paragraph>
          </>
        )}
        {!success && (
          <>
            {serviceType !== 'SMS' && (
              <>
                <Heading>Check your email for a code</Heading>
                <Text>
                  We have sent the code to{' '}
                  <Email href="#" onClick={onClose}>
                    <strong>{email}</strong> <EditIcon width={16} height={16} />
                  </Email>
                </Text>
              </>
            )}
            {serviceType === 'SMS' && (
              <>
                <Heading>Check your phone for a code</Heading>
                <Text>
                  We have sent the code to{' '}
                  <Email href="#" onClick={onClose}>
                    <strong>{phoneNumber}</strong>{' '}
                    <EditIcon width={16} height={16} />
                  </Email>
                </Text>
              </>
            )}
            <CodeItems>
              {loading && (
                <Loading>
                  <Loader inline noMargin />
                </Loading>
              )}
              {code
                .map((c, idx) => ({ c, idx }))
                .map(({ c, idx }) => (
                  <CodeItem
                    $shake={clearing}
                    type="number"
                    ref={(f) => {
                      refs.current[idx] = f
                    }}
                    key={`${idx}`}
                    min={0}
                    max={9}
                    step={1}
                    required
                    disabled={valid && !manual}
                    value={c}
                    onPaste={onPaste}
                    onChange={(e) => onChange(e, idx)}
                    onBlur={() => setManual(false)}
                    onKeyDown={onKeyDown}
                  />
                ))}
            </CodeItems>

            {!sent && message === 'code-mismatch' && (
              <Error>
                <ErrorText>
                  <strong>The code you entered was incorrect</strong>
                </ErrorText>
                <ErrorText>Please try again.</ErrorText>
              </Error>
            )}
            {message === 'error' && (
              <Error>
                <ErrorText>
                  <strong>Something went wrong with generating the code</strong>
                </ErrorText>
              </Error>
            )}
            {sent && (
              <Text>
                <strong>A new code has been sent to you</strong>
              </Text>
            )}
            <Text>
              Didn&apos;t receive the code?{' '}
              <Link href="#" onClick={onResend}>
                Resend code
              </Link>
            </Text>
          </>
        )}
      </Wrapper>
    </Background>
  )
}
